import type {
  AssetInput,
  CreatePostInput,
  LinkAttachmentInput,
  PostInputMetaData,
  ThreadedPostInput,
} from './graphqlTypes'
import type { SchedulingType } from './graphqlTypes'
import { getMixedMediaCapabilityForService } from '~publish/components/Composer/capability'
import { AttachmentTypes } from '~publish/legacy/constants'
import { Services } from '~publish/legacy/constants/services'

import type { Service } from '../graphqlEnums'
import type {
  ChannelVariant,
  OmniAsset,
  ThreadItem,
} from '../store/postSlice/types'
import {
  ensureLinkAttachmentProtocol,
  toLinkAttachmentInput,
} from './mapAssets'
import { mapShareMode } from './mapShareMode'
import { omniAssetToAssetInput } from './mapToDraftContentInput'
import { selectAssetsByPrecedence } from './selectAssetsByPrecedence'
import type { MapperContext } from './types'

/**
 * Maps an already-additive `OmniAsset[]` to the `assets` a service's
 * `CreatePostInput` actually sends. A link-kind entry means the link gate won
 * at projection time (@see selectAssetsByPrecedence) — its URL rides in
 * metadata instead, so `assets` goes empty. Otherwise mirrors `mapAssets.ts`:
 * mixed-media-capable channels send everything, others collapse to one kind
 * by precedence.
 */
export function mapChannelVariantAssets(
  assets: OmniAsset[],
  service: Service,
): AssetInput[] {
  if (assets.some((asset) => asset.kind === 'link')) {
    return []
  }

  const capability = getMixedMediaCapabilityForService(service)
  const selected = capability.canMixMediaTypes
    ? assets
    : selectAssetsByPrecedence(assets)
  const canTag = Services.get(service)?.canHaveUserTags ?? false

  return selected
    .filter(
      (asset): asset is Exclude<OmniAsset, { kind: 'link' }> =>
        asset.kind !== 'link',
    )
    .map((asset) =>
      omniAssetToAssetInput(asset, canTag, { includeThumbnailUrl: false }),
    )
}

/**
 * Same result as a Flux-based `mapLinkAttachment(draft)`, read off a mirrored
 * variant: the link-attachment gate (`enabledAttachmentType`) already decided
 * at mirror time whether the draft's link won, in which case it rides in
 * `variant.assets` as the sole `'link'`-kind entry — never alongside other
 * kinds.
 */
export function getChannelVariantLinkAttachment(
  variant: ChannelVariant,
): LinkAttachmentInput | undefined {
  if (variant.enabledAttachmentType !== AttachmentTypes.LINK) return undefined
  const linkAsset = variant.assets.find((asset) => asset.kind === 'link')
  return linkAsset ? toLinkAttachmentInput(linkAsset.link) : undefined
}

// Thread entries never send additive assets, unlike root content: mirrors
// `mapMediaAssets`'s unconditional precedence collapse in `mapThread.ts`,
// ignoring mixed-media capability.
function mapChannelVariantThreadItemAssets(
  assets: OmniAsset[],
  service: Service,
): AssetInput[] {
  if (assets.some((asset) => asset.kind === 'link')) {
    return []
  }

  const canTag = Services.get(service)?.canHaveUserTags ?? false
  return selectAssetsByPrecedence(assets)
    .filter(
      (asset): asset is Exclude<OmniAsset, { kind: 'link' }> =>
        asset.kind !== 'link',
    )
    .map((asset) =>
      omniAssetToAssetInput(asset, canTag, { includeThumbnailUrl: false }),
    )
}

/**
 * Maps `ChannelVariant.threadItems` to `ThreadedPostInput[]`, or `undefined`
 * for a non-thread variant (0 or 1 items). Mirrors `mapThread`'s per-item
 * link-attachment metadata for Threads/Bluesky: an item whose link gate won
 * carries its link in `metadata` instead of `assets`.
 */
export function mapChannelVariantThread(
  threadItems: ThreadItem[],
  service: Service,
): ThreadedPostInput[] | undefined {
  if (threadItems.length <= 1) return undefined

  return threadItems.map((item) => {
    const linkAsset = item.assets.find(
      (asset): asset is Extract<OmniAsset, { kind: 'link' }> =>
        asset.kind === 'link',
    )
    if (linkAsset && (service === 'threads' || service === 'bluesky')) {
      const linkAttachment = toLinkAttachmentInput(linkAsset.link)
      return {
        text: item.text,
        assets: [],
        metadata: {
          [service]: {
            linkAttachment:
              service === 'bluesky'
                ? ensureLinkAttachmentProtocol(linkAttachment)
                : linkAttachment,
          },
        },
      }
    }
    return {
      text: item.text,
      assets: mapChannelVariantThreadItemAssets(item.assets, service),
    }
  })
}

/**
 * Maps one `ChannelVariant` (+ context) to a `CreatePostInput`. `metadata` is
 * supplied by the caller, not derived here: the per-service mappers still
 * read editor-derived `Draft` fields (mentions, annotations, retweet-comment)
 * with no Zustand counterpart yet, and keeping this function pure keeps it
 * unit-testable like every other mapper here.
 *
 * `needsApproval` only ever sends `true`: it defaults to `false`
 * server-side, and `false` here would mean "withdraw" — meaningless on a
 * post that doesn't exist yet.
 */
export function channelVariantToCreatePostInput(
  variant: ChannelVariant,
  service: Service,
  metadata: PostInputMetaData,
  ctx: MapperContext,
): CreatePostInput {
  const { mode, dueAt, saveToDraft } = mapShareMode(ctx)

  // Reminder vs direct is per-channel: legacy `reminder`/`direct` → GraphQL
  // `notification`/`automatic`.
  const schedulingType: SchedulingType = variant.isReminder
    ? 'notification'
    : 'automatic'

  return {
    channelId: ctx.channelId,
    text: variant.text,
    mode,
    schedulingType,
    assets: mapChannelVariantAssets(variant.assets, service),
    tagIds: ctx.selectedTagIds,
    aiAssisted: variant.aiAssisted,
    metadata,
    ...(dueAt != null ? { dueAt } : {}),
    ...(saveToDraft != null ? { saveToDraft } : {}),
    ...(ctx.ideaId != null ? { ideaId: ctx.ideaId } : {}),
    ...(ctx.draftId != null ? { draftId: ctx.draftId } : {}),
    ...(ctx.source != null ? { source: ctx.source } : {}),
    ...(ctx.requestApproval === true ? { needsApproval: true } : {}),
  }
}
